import React, { useCallback } from 'react'
import { Button, Typography } from 'gu-ui-kit'
import renderInputField from 'components/FormControls/textInput'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import validator from 'utils/asyncValidator'
import { clearSubmitErrors, Field, Form, reduxForm } from 'redux-form'
import * as Yup from 'yup'
import { PropTypes } from 'prop-types'
import { Link } from 'react-router-dom'
import formStyles from 'modules/Forms/common-form.module.scss'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import passwordValidator from 'utils/validators/passwordValidator'
import styles from 'modules/User/Security/Security.module.scss'
import { useHistory } from 'react-router'
import { SET_NEW_USER_PASSWORD } from 'modules/User/Security/Password/types'

const validationSchema = Yup.object().shape({
  old_password: Yup.string().required('user::security.password.errors.oldPassword'),
  password: Yup.string()
    .test(
      'passwordValidator',
      'common::validation_messages.incorrect_password_format',
      passwordValidator,
    )
    .required('user::security.password.errors.password'),
  confirm: Yup.string()
    .oneOf([Yup.ref('password'), null], 'user::security.password.errors.confirmMatch')
    .required('user::security.password.errors.confirmEmpty'),
})

const Password = ({ handleSubmit, submitting }) => {
  const history = useHistory()
  const pathGen = useGeneratePathI18n()
  const dispatch = useDispatch()

  const { t } = useTranslation(['user'])

  const submit = () => {
    new Promise((resolve, reject) =>
      dispatch({
        type: SET_NEW_USER_PASSWORD,
        meta: { resolve, reject },
      }),
    ).then(() => history.push(pathGen(ROUTES.USER_SETTINGS)))
  }

  const handleChange = useCallback(() => dispatch(clearSubmitErrors('user/password')), [])

  return (
    <div>
      <div className={styles['security']}>
        <div className={styles['security__inner']}>
          <Typography variant={'h1'} className={styles['security__heading']}>
            {t('user::security.password.form.heading')}
          </Typography>
          <Form onSubmit={handleSubmit(submit)}>
            <div className={'margin-bottom-16'}>
              <Field
                fullWidth
                autoComplete="off"
                name="old_password"
                type={'password'}
                component={renderInputField}
                label={t('user::security.password.oldPassword')}
                placeholder={t('user::security.password.oldPassword')}
                onChange={handleChange}
                t={t}
                cleanButton
                revealButton
              />
            </div>
            <div className={'margin-bottom-16'}>
              <Field
                fullWidth
                autoComplete="off"
                name="password"
                type={'password'}
                component={renderInputField}
                label={t('user::security.password.password')}
                placeholder={t('user::security.password.password')}
                onChange={handleChange}
                t={t}
                cleanButton
                revealButton
              />
            </div>
            <div className={'margin-bottom-16'}>
              <Field
                fullWidth
                autoComplete="off"
                name="confirm"
                type={'password'}
                component={renderInputField}
                label={t('user::security.password.confirm')}
                placeholder={t('user::security.password.confirm')}
                onChange={handleChange}
                t={t}
                cleanButton
                revealButton
              />
            </div>

            <div className={formStyles['formControl__action-buttons']}>
              <Button
                disabled={submitting}
                component={Link}
                to={pathGen(ROUTES.USER_SETTINGS)}
                variant={'ghost'}
                grey
                size="l"
              >
                {t('common::form_buttons.cancel')}
              </Button>
              <Button
                fullWidth
                type="submit"
                bgColor="Label-GU-Primary"
                size="l"
                variant="primary"
                disabled={submitting}
              >
                {t('user::security.password.change')}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  )
}

Password.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
}

export default reduxForm({
  form: 'user/password',
  enableReinitialize: true,
  initialValues: {
    old_password: '',
    password: '',
    confirm: '',
  },
  shouldAsyncValidate: shouldAsyncValidate,
  asyncValidate: validator(validationSchema),
})(Password)
